import { useCallback, useRef } from 'react';
import type { WeldDrillDownRow } from '../components/dashboard/drilldown/drillDownTypes';
import { rowKey } from './weldDrillDownModel';
import {
  collectViolationsForWeld,
  fetchPassViewRecords,
  fetchProjectLimitsApi,
  fetchTiltViewRecords,
  fetchZoneViewRecords,
  type PassViewRecord,
} from './fetchWeldViewApis';
import {
  LIMITS_VIEW_BACKGROUND_MAX,
} from './limitsViewFetchQueue';
import { runWithConcurrency } from './runWithConcurrency';
import {
  REVIEW_PRIORITY_RANK,
  reviewPriorityFromAnomalies,
} from './weldReviewPriority';
import type { LimitsRow, WpsViolationRecord } from './wpsViolationModel';

export const WPS_BATCH_FETCH_CAP = 100;
/** First failed welds prioritized so KPI strip can fill quickly. */
export const WPS_KPI_FIRST_WAVE = 25;
/**
 * Welds processed in parallel. Each weld still requests pass+zone+tilt; those
 * GETs are capped globally by limitsViewFetchQueue (LIMITS_VIEW_MAX_IN_FLIGHT).
 */
const BATCH_CONCURRENCY = LIMITS_VIEW_BACKGROUND_MAX;
/** Flush progress after each of the first N welds, then every N thereafter. */
const PROGRESS_FLUSH_AFTER_FIRST_WAVE = WPS_KPI_FIRST_WAVE;
const PROGRESS_FLUSH_EVERY = 5;

export interface WpsViolationsBatchResult {
  violations: Record<string, WpsViolationRecord[]>;
  welders: Record<string, string>;
  passes: Record<string, PassViewRecord[]>;
  /** Limits used for this batch (set when the loader had to fetch them). */
  limits?: LimitsRow[];
}

export type WpsViolationsBatchProgress = (partial: WpsViolationsBatchResult) => void;

function resultFromViews(
  row: WeldDrillDownRow,
  limitsRows: LimitsRow[],
  passes: PassViewRecord[],
  zones: Awaited<ReturnType<typeof fetchZoneViewRecords>>,
  tilts: Awaited<ReturnType<typeof fetchTiltViewRecords>>,
): {
  key: string;
  violations: WpsViolationRecord[];
  welderIds: string;
  passes: PassViewRecord[];
} {
  const violations = collectViolationsForWeld(passes, zones, tilts, limitsRows);
  const welderIds = Array.from(
    new Set(passes.map((p) => p.welderId).filter((id) => id && id !== '—')),
  ).join(', ');
  return { key: rowKey(row), violations, welderIds: welderIds || '—', passes };
}

function shouldFlushProgress(completed: number, total: number): boolean {
  if (completed <= PROGRESS_FLUSH_AFTER_FIRST_WAVE) return true;
  if (completed === total) return true;
  return completed % PROGRESS_FLUSH_EVERY === 0;
}

/** Critical / High first so Failed params and KPIs fill for worst welds sooner. */
export function sortFailedRowsByPriority(rows: WeldDrillDownRow[]): WeldDrillDownRow[] {
  return [...rows].sort((a, b) => {
    const pa = reviewPriorityFromAnomalies(a.anomalies);
    const pb = reviewPriorityFromAnomalies(b.anomalies);
    const ra = REVIEW_PRIORITY_RANK[pa] ?? 99;
    const rb = REVIEW_PRIORITY_RANK[pb] ?? 99;
    return ra - rb;
  });
}

export function useWpsViolationsBatchLoader() {
  const batchRunRef = useRef(0);

  const loadBatch = useCallback(
    async (
      projectId: number,
      rows: WeldDrillDownRow[],
      /** `null` = not loaded yet; array (including empty) = already resolved for this session. */
      limitsRows: LimitsRow[] | null,
      existingKeys: Set<string>,
      onProgress?: WpsViolationsBatchProgress,
    ): Promise<WpsViolationsBatchResult> => {
      const runId = batchRunRef.current + 1;
      batchRunRef.current = runId;

      const toFetch = rows
        .filter((r) => r.outcome === 'fail' && !existingKeys.has(rowKey(r)))
        .slice(0, WPS_BATCH_FETCH_CAP);

      const violations: Record<string, WpsViolationRecord[]> = {};
      const welders: Record<string, string> = {};
      const passes: Record<string, PassViewRecord[]> = {};

      let pendingViolations: Record<string, WpsViolationRecord[]> = {};
      let pendingWelders: Record<string, string> = {};
      let pendingPasses: Record<string, PassViewRecord[]> = {};
      let completed = 0;

      const flushProgress = () => {
        if (!onProgress) return;
        if (
          Object.keys(pendingViolations).length === 0 &&
          Object.keys(pendingWelders).length === 0 &&
          Object.keys(pendingPasses).length === 0
        ) {
          return;
        }
        onProgress({
          violations: pendingViolations,
          welders: pendingWelders,
          passes: pendingPasses,
        });
        pendingViolations = {};
        pendingWelders = {};
        pendingPasses = {};
      };

      let fetchedLimits: LimitsRow[] | undefined;
      const limitsPromise =
        limitsRows === null
          ? fetchProjectLimitsApi(projectId).then((limitsData) => {
              fetchedLimits = limitsData.allDataSet;
              return limitsData.allDataSet;
            })
          : Promise.resolve(limitsRows);

      const accept = (result: {
        key: string;
        violations: WpsViolationRecord[];
        welderIds: string;
        passes: PassViewRecord[];
      }) => {
        violations[result.key] = result.violations;
        welders[result.key] = result.welderIds;
        passes[result.key] = result.passes;
        pendingViolations[result.key] = result.violations;
        pendingWelders[result.key] = result.welderIds;
        pendingPasses[result.key] = result.passes;
        completed += 1;
        if (shouldFlushProgress(completed, toFetch.length)) {
          flushProgress();
        }
      };

      const fetchOne = async (row: WeldDrillDownRow, priority: number) => {
        if (batchRunRef.current !== runId) return;
        const params = {
          project_id: projectId,
          project_name: row.projectName || undefined,
          weld_number: row.weldNumber,
          job_number: row.jobNumber || undefined,
        };
        const slot = { priority };
        try {
          const limitsForRow = await limitsPromise;
          const rowPasses = await fetchPassViewRecords(params, slot);
          if (batchRunRef.current !== runId) return;
          const key = rowKey(row);
          const welderIds = Array.from(
            new Set(rowPasses.map((pass) => pass.welderId).filter((id) => id && id !== '—')),
          ).join(', ');
          passes[key] = rowPasses;
          welders[key] = welderIds || '—';
          pendingPasses[key] = rowPasses;
          pendingWelders[key] = welderIds || '—';
          flushProgress();
          const [zones, tilts] = await Promise.all([
            fetchZoneViewRecords(params, slot),
            fetchTiltViewRecords(params, slot),
          ]);
          if (batchRunRef.current !== runId) return;
          accept(resultFromViews(row, limitsForRow, rowPasses, zones, tilts));
        } catch {
          // Leave this weld out of the cache so clicking the row can try again.
        }
      };

      await runWithConcurrency(toFetch, BATCH_CONCURRENCY, async (row) => {
        const priority = row === toFetch[0] ? 0 : 1;
        await fetchOne(row, priority);
      });

      if (batchRunRef.current === runId) {
        flushProgress();
      }

      return { violations, welders, passes, limits: fetchedLimits };
    },
    [],
  );

  const cancelBatch = useCallback(() => {
    batchRunRef.current += 1;
  }, []);

  return { loadBatch, cancelBatch };
}
